import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, GripVertical, Settings2, Camera } from 'lucide-react';
import { createWorkflow, updateWorkflow, ServiceWorkflow, WorkflowStep as DBWorkflowStep } from '../lib/workflows';
import { useBusiness } from '../contexts/BusinessContext';
import WorkflowStepLibraryModal from './WorkflowStepLibraryModal';
import AdvancedRulesModal from './AdvancedRulesModal';

interface DefaultResource {
  id: string;
  name: string;
}

interface WorkflowStep {
  id: string;
  title: string;
  order: number;
  workflow_step_library_id?: string;
  description?: string;
  category?: string;
  default_resources?: DefaultResource[];
  photos_enabled?: boolean;
}

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  serviceName: string;
  onSuccess?: () => void;
  existingWorkflow?: ServiceWorkflow | null;
  existingSteps?: DBWorkflowStep[];
}

export default function WorkflowModal({ 
  isOpen, 
  onClose,
  serviceId,
  serviceName,
  onSuccess,
  existingWorkflow,
  existingSteps
}: WorkflowModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [steps, setSteps] = useState<WorkflowStep[]>([]);
  const [workflowName, setWorkflowName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStepLibrary, setShowStepLibrary] = useState(false);
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const [selectedStepForRules, setSelectedStepForRules] = useState<WorkflowStep | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Reset form when opening
      setError(null);
      
      if (existingWorkflow && existingSteps) {
        // Edit mode - populate with existing data
        console.log('📥 Loading existing workflow steps:', existingSteps.map(step => ({
          id: step.id,
          title: step.title,
          photos_enabled: step.photos_enabled,
          hasPhotosField: 'photos_enabled' in step
        })));
        
        setWorkflowName(existingWorkflow.name);
        setSteps(existingSteps.map(step => ({
          id: step.id,
          title: step.title,
          description: step.description,
          category: step.category,
          workflow_step_library_id: step.workflow_step_library_id,
          order: step.order_index,
          default_resources: step.default_resources || [],
          photos_enabled: step.photos_enabled || false
        })));
      } else {
        // Create mode - reset to defaults
        setWorkflowName(serviceName || '');
        setSteps([]);
      }
    }
  }, [isOpen, serviceName, existingWorkflow, existingSteps]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const addStep = () => {
    setShowStepLibrary(true);
  };

  const handleSelectStep = (stepData: { id: string; title: string; description?: string; category?: string }) => {
    const newStep: WorkflowStep = {
      id: `new-${Date.now()}`,
      title: stepData.title,
      description: stepData.description,
      category: stepData.category,
      workflow_step_library_id: stepData.id,
      order: steps.length + 1
    };
    setSteps([...steps, newStep]);
    setShowStepLibrary(false);
  };



  const deleteStep = (id: string) => {
    const updatedSteps = steps.filter(step => step.id !== id);
    // Reorder remaining steps
    const reorderedSteps = updatedSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    setSteps(reorderedSteps);
  };

  const handleSettingsClick = (step: WorkflowStep) => {
    setSelectedStepForRules(step);
    setShowAdvancedRules(true);
  };

  const handleResourceAssignment = (resources: DefaultResource[]) => {
    if (selectedStepForRules) {
      setSteps(steps.map(step => 
        step.id === selectedStepForRules.id 
          ? { 
              ...step, 
              default_resources: resources 
            }
          : step
      ));
      setShowAdvancedRules(false);
      setSelectedStepForRules(null);
    }
  };

  const handlePhotosChange = (enabled: boolean) => {
    if (selectedStepForRules) {
      const stepId = selectedStepForRules.id;
      setSteps(prevSteps => prevSteps.map(step => 
        step.id === stepId 
          ? { 
              ...step, 
              photos_enabled: enabled 
            }
          : step
      ));
      console.log('📸 Photos enabled changed for step:', stepId, 'to:', enabled);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    setSaving(true);
    setError(null);

    // Debug: Log the steps data before saving
    console.log('💾 Saving workflow with steps:', steps.map(step => ({
      id: step.id,
      title: step.title,
      photos_enabled: step.photos_enabled
    })));

    try {
      if (existingWorkflow) {
        // Update existing workflow
        await updateWorkflow(existingWorkflow.id, {
          name: workflowName,
          steps: steps.map((step, index) => ({
            id: step.id.startsWith('new-') ? undefined : step.id, // Only include ID for existing steps
            title: step.title,
            description: step.description,
            workflow_step_library_id: step.workflow_step_library_id,
            order_index: index + 1,
            default_resources: step.default_resources,
            photos_enabled: step.photos_enabled || false
          }))
        });
      } else {
        // Create new workflow
        const workflowData = {
          service_id: serviceId,
          business_id: selectedBusiness.id,
          name: workflowName,
          steps: steps.map((step, index) => ({
            title: step.title,
            description: step.description,
            workflow_step_library_id: step.workflow_step_library_id,
            order_index: index + 1,
            default_resources: step.default_resources,
            photos_enabled: step.photos_enabled || false
          }))
        };

        await createWorkflow(workflowData);
      }
      
      // Success
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className={`fixed inset-0 bg-gray-500 transition-opacity duration-150 ${
            isAnimating ? 'bg-opacity-75' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div
          className={`inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all duration-150 sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full ${
            isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
          }`}
        >
          {/* Header */}
          <div className="px-4 pt-5 pb-4 sm:p-6 bg-gradient-to-br from-purple-50 to-blue-50 border-b border-purple-200/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {existingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {existingWorkflow ? 'Edit the workflow template for' : 'Create a workflow template for'} {serviceName}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="rounded-xl text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 p-2 hover:bg-gray-100/80 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Workflow Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Name
              </label>
              <input
                type="text"
                value={workflowName}
                readOnly
                className="w-full px-4 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>

            {/* Steps */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Workflow Steps
              </label>

              {steps.length === 0 ? (
                <div className="flex justify-center">
                  <button
                    onClick={addStep}
                    className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all duration-200"
                    title="Add step"
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center text-gray-400">
                        <GripVertical className="h-5 w-5" />
                        <span className="ml-2 text-sm font-medium text-gray-600">
                          {index + 1}.
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900">{step.title}</h4>
                          {step.photos_enabled && (
                            <div className="flex items-center justify-center w-5 h-5 rounded bg-blue-100 text-blue-600" title="Photo capture enabled">
                              <Camera className="h-3 w-3" />
                            </div>
                          )}
                          {step.category && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">
                              {step.category}
                            </span>
                          )}
                        </div>
                        {step.description && (
                          <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                        )}
                        {step.default_resources && step.default_resources.length > 0 && (
                          <p className="mt-1 text-xs text-blue-600 flex items-center">
                            <Settings2 className="h-3 w-3 mr-1" />
                            Defaults: {step.default_resources.map(r => r.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSettingsClick(step)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Advanced settings"
                        >
                          <Settings2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteStep(step.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete step"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* Add Step Button */}
                  <div className="flex justify-center mt-2">
                    <button
                      onClick={addStep}
                      className="p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all duration-200"
                      title="Add step"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <button
              type="button"
              onClick={handleSave}
              disabled={!workflowName || steps.length === 0 || steps.some(step => !step.title) || saving}
              className="w-full inline-flex justify-center rounded-2xl border border-transparent shadow-sm px-6 py-3 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Workflow'
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-2xl border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {showStepLibrary && createPortal(
        <WorkflowStepLibraryModal
          isOpen={true}
          onClose={() => setShowStepLibrary(false)}
          onSelectStep={handleSelectStep}
        />,
        document.body
      )}
      {showAdvancedRules && selectedStepForRules && createPortal(
        <AdvancedRulesModal
          isOpen={true}
          onClose={() => {
            setShowAdvancedRules(false);
            setSelectedStepForRules(null);
          }}
          stepTitle={selectedStepForRules.title}
          defaultResources={selectedStepForRules.default_resources || []}
          onAssignResources={handleResourceAssignment}
          photosEnabled={selectedStepForRules.photos_enabled || false}
          onPhotosChange={handlePhotosChange}
        />,
        document.body
      )}
    </>
  );
}